import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Card, Form, Button, Spinner, Table, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import {
    FaSearch, FaPrint, FaMoneyBillWave,
    FaChartLine, FaFilter, FaTimes,
    FaCalendarAlt, FaUser, FaCreditCard,
    FaWallet, FaShoppingCart, FaStethoscope, FaMoneyBill
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function DailyClosingReport() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [previousPayments, setPreviousPayments] = useState([]);

    const [summary, setSummary] = useState({
        total_sales: 0,
        total_appointments: 0,
        total_expenses: 0,
        total_paid: 0,
        total_pending: 0
    });
    const [paymentsByMethod, setPaymentsByMethod] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [dateRange, setDateRange] = useState({ start_date: '', end_date: '' });
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const formatDateForAPI = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.start_date = formatDateForAPI(startDate);
            if (endDate) params.end_date = formatDateForAPI(endDate);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/daily-closing-report`,
                {
                    params: params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.success) {
                setPreviousPayments(response.data.previousSalePayments || []);
                setReportData(response.data.data || []);
                setSummary(response.data.summary || {
                    total_sales: 0,
                    total_appointments: 0,
                    total_expenses: 0,
                    total_paid: 0,
                    total_pending: 0
                });
                setPaymentsByMethod(response.data.payments_by_method || []);
                setDateRange(response.data.date_range || { start_date: '', end_date: '' });
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            alert(t('failed_to_load_report'));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, t]);

    // Initialize dates and fetch report
    useEffect(() => {
        const today = new Date();
        const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
        setStartDate(utcToday);
        setEndDate(utcToday);
        setIsInitialLoad(false);
    }, []);

    // Fetch report when dates are initialized or changed
    useEffect(() => {
        if (!isInitialLoad && startDate && endDate) {
            fetchReport();
        }
    }, [startDate, endDate, isInitialLoad, fetchReport]);

    const handleFilter = () => {
        fetchReport();
    };

    const handleReset = () => {
        const today = new Date();
        const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
        setStartDate(utcToday);
        setEndDate(utcToday);
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        return parseFloat(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const exportToPDF = () => {
        let url = `${process.env.REACT_APP_URL}/report/daily-closing-report`;
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", formatDateForAPI(startDate));
        if (endDate) params.append("end_date", formatDateForAPI(endDate));
        const finalUrl = `${url}?${params.toString()}`;
        window.open(finalUrl, "_blank");
    };

    const handleStartDateChange = (date) => {
        if (date) {
            const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            setStartDate(utcDate);
        } else {
            setStartDate(null);
        }
    };

    const handleEndDateChange = (date) => {
        if (date) {
            const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            setEndDate(utcDate);
        } else {
            setEndDate(null);
        }
    };

    // Filter data by type
    const salesData = reportData.filter(item => item.type === 'Sale');
    const appointmentsData = reportData.filter(item => item.type === 'Appointment');
    const expensesData = reportData.filter(item => item.type === 'Expense');

    const isAllZero =
        summary.cash_in_hand === 0 &&
        summary.cash_out === 0;



    // Sales Table Component - Using Bootstrap Table
    const SalesTable = () => (
        <div className="mb-4 p-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    <FaShoppingCart className="text-primary me-2" />
                    {t('Sales Transactions')}
                    {salesData.length > 0 && <Badge bg="primary" className="ms-2">{salesData.length}</Badge>}
                </h5>
            </div>
            <div className="table-responsive-wrapper">
                <Table responsive hover className="table mb-0">
                    <thead>
                        <tr>
                            <th className="text-center">#</th>
                            <th>Sale ID</th>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Products</th>
                            <th className="text-end">Paid Amount</th>
                            <th>Payment Methods</th>
                        </tr>
                    </thead>
                    <tbody>
                        {salesData.map((sale, idx) => {
                            // Normalize payments to always be an array
                            const paymentsArray = Array.isArray(sale.payments)
                                ? sale.payments
                                : sale.payments
                                    ? Object.values(sale.payments)
                                    : [];

                            return (
                                <tr key={idx}>
                                    <td className="text-center">{idx + 1}</td>
                                    <td>
                                        <strong className="text-primary">{sale.id}</strong>
                                    </td>
                                    <td>{formatDate(sale.date)}</td>
                                    <td>{sale.customer || '-'}</td>
                                    <td>
                                        <div className="d-flex flex-wrap gap-1">
                                            {sale.details?.map((d, i) => (
                                                <span key={i} className="badge bg-light text-dark">
                                                    {d.name} × {d.qty}
                                                    {sale.customer_id != 1 && (
                                                        <> ( {d.amount} )</>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="text-end fw-bold text-success">
                                        {sale.customer_id == 1
                                            ? '-'
                                            : sale.paid_amount > 0
                                                ? formatCurrency(sale.paid_amount)
                                                : sale.appointment_number
                                                    ? sale.appointment_number
                                                    : '-'}
                                    </td>
                                    <td>
                                        {paymentsArray.length > 0 ? (
                                            paymentsArray.map((p, i) => (
                                                <div key={i} className="small">
                                                    {p.method === "Merged Payment" ? (
                                                        <>
                                                            <strong>Merged Payment</strong>
                                                            {p.sale_no && <span> with {p.sale_no}</span>}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong>{p.method}:</strong>{" "}
                                                            {p.amount < 0 ? (
                                                                <>Refund: {Math.abs(p.amount)}</>
                                                            ) : (
                                                                p.amount
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    {salesData.length > 0 && (
                        <tfoot className="table-active">
                            <tr>
                                <td colSpan="5" className="text-end fw-bold">Total Sales:</td>
                                <td className="text-end fw-bold text-success">
                                    {formatCurrency(salesData.reduce((sum, s) => sum + (s.paid_amount || 0), 0))}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </Table>
            </div>
            {salesData.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <FaShoppingCart size={40} className="mb-2 opacity-25" />
                    <p className="mb-0">No sales found for this period</p>
                </div>
            )}
        </div>
    );

    // Appointments Table Component - Using Bootstrap Table
    const AppointmentsTable = () => (
        <div className="mb-4 p-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    <FaStethoscope className="text-info me-2" />
                    {t('Appointment Transactions')}
                    {appointmentsData.length > 0 && <Badge bg="info" className="ms-2">{appointmentsData.length}</Badge>}
                </h5>
            </div>
            <div className="table-responsive-wrapper">
                <Table responsive hover className="table mb-0">
                    <thead>
                        <tr>
                            <th className="text-center" >#</th>
                            <th >Appointment ID</th>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Pet</th>
                            <th>Services</th>
                            <th className="text-end">Paid Amount</th>
                            <th>Payment Methods</th>
                        </tr>
                    </thead>
                    <tbody>
                        {appointmentsData.map((appointment, idx) => (
                            <tr key={idx}>
                                <td className="text-center">{idx + 1}</td>
                                <td>
                                    <strong className="text-primary">{appointment.id}</strong>
                                </td>
                                <td>{formatDate(appointment.date)}</td>
                                <td>{appointment.customer || '-'}</td>
                                <td>{appointment.pet || '-'}</td>
                                <td>
                                    <div className="d-flex flex-wrap gap-1">
                                        {appointment.details?.flatMap((d, i) => {

                                            // Agar sale ke products hain to sirf products show karo
                                            if (d.type === "Sale" && d.products?.length > 0) {
                                                return d.products.map((p, pi) => (
                                                    <span
                                                        key={`p-${i}-${pi}`}
                                                        className="badge bg-secondary text-white"
                                                    >
                                                        <strong>{d.type}</strong> {p.name} -{" "}
                                                        {p.price ? formatCurrency(p.price) : "-"}
                                                    </span>
                                                ));
                                            }

                                            // Normal details
                                            return (
                                                <span key={`d-${i}`} className="badge bg-light text-dark">
                                                    <strong>{d.type}</strong> {d.name} -{" "}
                                                    {d.price ? formatCurrency(d.price) : "-"}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </td>
                                <td className="text-end fw-bold text-success">
                                    {appointment.customer_id == 1
                                        ? '-'
                                        : appointment.paid_amount > 0
                                            ? formatCurrency(appointment.paid_amount)
                                            : '-'}
                                </td>
                                <td>
                                    {appointment.payments?.map((p, i) => (
                                        <div key={i} className="small">
                                            <strong>{p.method}:</strong> {formatCurrency(p.amount)}
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {appointmentsData.length > 0 && (
                        <tfoot className="table-active">
                            <tr>
                                <td colSpan="6" className="text-end fw-bold">Total Appointments:</td>
                                <td className="text-end fw-bold text-success">
                                    {formatCurrency(appointmentsData.reduce((sum, a) => sum + (a.paid_amount || 0), 0))}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </Table>
            </div>
            {appointmentsData.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <FaStethoscope size={40} className="mb-2 opacity-25" />
                    <p className="mb-0">No appointments found for this period</p>
                </div>
            )}
        </div>
    );

    // Expenses Table Component - Using Bootstrap Table
    const ExpensesTable = () => (
        <div className="mb-4 p-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    <FaMoneyBillWave className="text-danger me-2" />
                    {t('Expense Transactions')}
                    {expensesData.length > 0 && <Badge bg="danger" className="ms-2">{expensesData.length}</Badge>}
                </h5>
            </div>
            <div className="table-responsive-wrapper">
                <Table responsive hover className="table mb-0">
                    <thead>
                        <tr>
                            <th className="text-center" style={{ width: '5%' }}>#</th>
                            <th style={{ width: '15%' }}>Date</th>
                            <th style={{ width: '35%' }}>Expense Items</th>
                            <th className="text-end" style={{ width: '20%' }}>Paid Amount</th>
                            <th style={{ width: '25%' }}>Payment Methods</th>
                        </tr>
                    </thead>
                    <tbody>
                        {expensesData.map((expense, idx) => (
                            <tr key={idx}>
                                <td className="text-center">{idx + 1}</td>
                                <td>{formatDate(expense.date)}</td>
                                <td>
                                    <div className="d-flex flex-wrap gap-1">
                                        {expense.details?.map((d, i) => (
                                            <span key={i} className="badge bg-light text-dark">
                                                {d.name}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="text-end fw-bold text-danger">{formatCurrency(expense.paid_amount)}</td>
                                <td>
                                    {expense.payments?.map((p, i) => (
                                        <div key={i} className="small">
                                            <strong>{p.method}:</strong> {formatCurrency(p.amount)}
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {expensesData.length > 0 && (
                        <tfoot className="table-active">
                            <tr>
                                <td colSpan="3" className="text-end fw-bold">Total Expenses:</td>
                                <td className="text-end fw-bold text-danger">
                                    {formatCurrency(expensesData.reduce((sum, e) => sum + (e.paid_amount || 0), 0))}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </Table>
            </div>
            {expensesData.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <FaMoneyBillWave size={40} className="mb-2 opacity-25" />
                    <p className="mb-0">No expenses found for this period</p>
                </div>
            )}
        </div>
    );

    // Show loading only on initial load or when fetching
    if (loading && isInitialLoad) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">{t('Loading...')}</p>
                </div>
            </PageLayout>
        );
    }

    const previousPaymentsTotal = previousPayments.reduce(
        (sum, payment) => sum + Number(payment.amount || 0),
        0
    );

    const previousCashPaymentsTotal = previousPayments.reduce((sum, payment) => {
        if (payment.method?.name === "Cash") {
            return sum + Number(payment.amount || 0);
        }
        return sum;
    }, 0);

    const net = (summary.cash_in_hand + previousCashPaymentsTotal) - summary.cash_out;
    return (
        <PageLayout>
            <div className="mb-4">
                <h2 className="mb-1 fw-bold text-dark">
                    <FaChartLine className="me-2 text-primary" />
                    {t('Daily Closing Report')}
                </h2>
                <p className="text-muted mb-0">{t('View daily transactions, payments, and financial summary')}</p>
                {dateRange.start_date && dateRange.end_date && (
                    <p className="text-muted small mt-1">
                        {t('Report Period')}: {formatDate(dateRange.start_date)} - {formatDate(dateRange.end_date)}
                    </p>
                )}
            </div>

            {/* Filter Section */}
            <Card className="shadow-sm mb-4 border-0">
                <Card.Body className="p-3 p-md-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h5 className="mb-0 d-flex align-items-center gap-2 fs-6 fw-bold text-primary">
                            <FaFilter size={14} />
                            {t('Filters')}
                        </h5>
                        <Button
                            variant="link"
                            onClick={() => setShowFilters(!showFilters)}
                            className="text-decoration-none p-0"
                            size="sm"
                        >
                            {showFilters ? <FaTimes /> : <FaFilter />}
                        </Button>
                    </div>

                    {showFilters && (
                        <Row className="g-3 align-items-end">
                            <Col xs={12} md={2}>
                                <Form.Label className="fw-semibold small mb-1">{t('Start Date')}</Form.Label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleStartDateChange}
                                    className="form-control form-control-sm rounded-3"
                                    placeholderText="Select start date"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                    maxDate={endDate || new Date()}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <Form.Label className="fw-semibold small mb-1">{t('End Date')}</Form.Label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleEndDateChange}
                                    className="form-control form-control-sm rounded-3"
                                    placeholderText="Select end date"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                    minDate={startDate}
                                    maxDate={new Date()}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleFilter}
                                        disabled={loading}
                                        className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                                        size="sm"
                                    >
                                        <FaSearch size={12} />
                                        {loading ? t('Loading...') : t('Search')}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleReset}
                                        disabled={loading}
                                        size="sm"
                                    >
                                        {t('Reset')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            <Row className="g-3 mb-4">
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Total Sales</h6>
                            <h4 className="mb-0 text-success fw-bold">{formatCurrency(summary.total_sales)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Total Appointment</h6>
                            <h4 className="mb-0 text-danger fw-bold">{formatCurrency(summary.total_appointments)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Total Expenses</h6>
                            <h4 className="mb-0 text-danger fw-bold">{formatCurrency(summary.total_expenses)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Previous Payments</h6>
                            <h4 className="mb-0 text-danger fw-bold">{formatCurrency(previousPaymentsTotal)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Total Paid</h6>
                            <h4 className="mb-0 text-primary fw-bold">{formatCurrency((summary.total_paid) + previousPaymentsTotal)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">

                            </div>
                            <h6 className="mb-1 text-muted small">Cash Flow</h6>
                            {isAllZero ? (
                                <h4 className="mb-0 text-primary fw-bold">
                                    {formatCurrency(0)}
                                </h4>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-success fw-bold">
                                            ↓ {formatCurrency(summary.cash_in_hand) + previousCashPaymentsTotal}
                                        </span>
                                        <span className="text-danger fw-bold">
                                            ↑ {formatCurrency(summary.cash_out)}
                                        </span>
                                        <span className="text-muted">=</span>
                                        <span className={`fw-bold ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {net >= 0 ? '↓' : '↑'} {formatCurrency(net)}
                                        </span>
                                    </div>
                                </>
                            )}




                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Payments by Method Summary */}
            {paymentsByMethod.length > 0 && (
                <Row className="g-3 mb-4">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-3">
                                <h6 className="mb-3 fw-bold">{t('Payments by Method')}</h6>
                                <Row className="g-2">
                                    {paymentsByMethod.map((method, idx) => (
                                        <Col xs={6} md={3} key={idx}>
                                            <Card className="border-0 bg-light h-100">
                                                <Card.Body className="p-2 p-md-3 text-center">
                                                    <div className="mb-2">
                                                        {method.name === 'Cash' && <FaMoneyBillWave className="text-success" size={24} />}
                                                        {method.name === 'Meezan' && <FaCreditCard className="text-primary" size={24} />}
                                                        {method.name === 'Alflah' && <FaWallet className="text-warning" size={24} />}
                                                        {!['Cash', 'Meezan', 'Alflah'].includes(method.name) && <FaMoneyBill className="text-secondary" size={24} />}
                                                    </div>
                                                    <div className="fw-bold h5 mb-1">{formatCurrency(method.total)}</div>
                                                    <div className="small text-muted">{method.name}</div>
                                                    <small className="text-muted">{method.count} {t('transactions')}</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Action Buttons */}
            <div className="mb-3 d-flex justify-content-end no-print">
                <Button variant="danger" onClick={exportToPDF} size="sm" className="d-flex align-items-center gap-2">
                    <FaPrint size={14} />
                    {t('Export to PDF')}
                </Button>
            </div>

            {/* Main Transactions Tables - Conditional Rendering */}
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">{t('Loading...')}</p>
                </div>
            ) : (
                <>
                    {/* Only show Sales table if data exists */}
                    {salesData.length > 0 && <SalesTable />}

                    {/* Only show Appointments table if data exists */}
                    {appointmentsData.length > 0 && <AppointmentsTable />}

                    {/* Only show Expenses table if data exists */}
                    {expensesData.length > 0 && <ExpensesTable />}

                    {/* Show message if no data at all */}
                    {salesData.length === 0 && appointmentsData.length === 0 && expensesData.length === 0 && (
                        <Card className="shadow-sm border-0">
                            <Card.Body className="text-center py-5">
                                <FaChartLine size={48} className="text-muted mb-3 opacity-25" />
                                <p className="text-muted mb-0">{t('No transactions found for this period')}</p>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}

            <style>{`
                
            `}</style>
        </PageLayout>
    );
}